import { useEffect, useRef, useState } from 'react';

interface NaverDynamicMapProps {
  width?: string;
  height?: string;
  className?: string;
}

declare global {
  interface Window {
    naver: any;
  }
}

export function NaverDynamicMap({ width = '100%', height = '384px', className = '' }: NaverDynamicMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // 벅스어드벤처 키즈카페 위치 (대구시 달서구 조암남로32길 20-4)
    const lat = 35.8294;
    const lng = 128.5322;

    // Check if running on localhost - Naver Maps API doesn't work on localhost
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.startsWith('192.168.') ||
                       window.location.hostname.startsWith('10.');
    
    if (isLocalhost) {
      console.log('Running on localhost - showing fallback UI');
      console.log('로컬 개발 환경에서는 네이버 지도가 표시되지 않습니다.');
      setHasError(true);
      setIsLoading(false);
      return;
    }

    // 배포 환경 도메인 로깅
    console.log('현재 도메인:', window.location.hostname);
    console.log('프로토콜:', window.location.protocol);

    const initMap = () => {
      if (!mapRef.current) return;

      try {
        // 지도 옵션
        const mapOptions = {
          center: new window.naver.maps.LatLng(lat, lng),
          zoom: 16,
          zoomControl: true,
          zoomControlOptions: {
            position: window.naver.maps.Position.TOP_RIGHT
          },
          mapTypeControl: false,
          scaleControl: false,
          logoControl: false,
          mapDataControl: false
        };

        // 지도 생성
        const map = new window.naver.maps.Map(mapRef.current, mapOptions);

        // 마커 추가
        const marker = new window.naver.maps.Marker({
          position: new window.naver.maps.LatLng(lat, lng),
          map: map,
          title: '벅스어드벤처 디지털 키즈카페',
          animation: window.naver.maps.Animation.DROP
        });

        // 정보창 추가
        const infoWindow = new window.naver.maps.InfoWindow({
          content: `
            <div style="padding: 12px; min-width: 200px;">
              <h4 style="margin: 0 0 8px 0; font-weight: bold; font-size: 14px;">벅스어드벤처 디지털 키즈카페</h4>
              <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">대구시 달서구 조암남로32길 20-4 6층</p>
              <p style="margin: 0; font-size: 12px; color: #ff6b35; font-weight: bold;">📞 053-642-5533</p>
            </div>
          `,
          maxWidth: 300,
          backgroundColor: "#fff",
          borderColor: "#ff6b35",
          borderWidth: 2,
          anchorSize: new window.naver.maps.Size(20, 20),
          anchorSkew: true,
          anchorColor: "#fff",
          pixelOffset: new window.naver.maps.Point(0, -10)
        });

        // 마커 클릭 시 정보창 열기
        window.naver.maps.Event.addListener(marker, 'click', () => {
          if (infoWindow.getMap()) {
            infoWindow.close();
          } else {
            infoWindow.open(map, marker);
          }
        });

        // 초기에 정보창 열기
        infoWindow.open(map, marker);

        setIsLoading(false);
      } catch (error) {
        console.error('네이버 지도 초기화 실패:', error);
        console.error('에러 상세:', {
          error,
          naverExists: !!window.naver,
          mapsExists: !!window.naver?.maps,
          domain: window.location.hostname
        });
        setHasError(true);
        setIsLoading(false);
      }
    };

    // 네이버 지도 API 로드 확인 및 초기화
    const checkAndInitMap = () => {
      // 인증 실패 콜백 확인
      if ((window as any).navermap_authFailure) {
        console.log('네이버 지도 인증 실패 핸들러가 등록되어 있습니다.');
      }

      if (window.naver && window.naver.maps) {
        // API가 이미 로드됨
        console.log('네이버 지도 API 사용 가능, 지도 초기화 시작');
        initMap();
      } else {
        // API 로드 대기
        console.log('네이버 지도 API 로딩 대기 중...');
        let attempts = 0;
        const maxAttempts = 100; // 10초 대기로 증가
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.naver && window.naver.maps) {
            clearInterval(checkInterval);
            console.log(`네이버 지도 API 로드 완료 (${attempts * 100}ms 소요)`);
            initMap();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            console.error('네이버 지도 API 로드 타임아웃 (10초)');
            console.log('가능한 원인:');
            console.log('1. 네이버 클라우드 플랫폼에 도메인이 등록되지 않음');
            console.log('2. Client ID가 올바르지 않음');
            console.log('3. 네트워크 문제');
            setHasError(true);
            setIsLoading(false);
          }
        }, 100);
      }
    };

    checkAndInitMap();
  }, []);

  if (hasError) {
    // 지도 API 사용 불가 시 대체 UI - 더 매력적인 디자인
    return (
      <div 
        style={{ width, height }} 
        className={`rounded-2xl overflow-hidden border border-[#eaeaea] relative ${className}`}
      >
        {/* 배경 그라데이션 */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50" />
        
        {/* 장식용 원형 요소들 */}
        <div className="absolute top-4 right-4 w-32 h-32 bg-yellow-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-4 left-4 w-40 h-40 bg-orange-200/30 rounded-full blur-3xl" />
        
        {/* 메인 콘텐츠 */}
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl p-8 text-center max-w-md w-full shadow-xl border border-white/50">
            {/* 위치 아이콘 */}
            <div className="w-16 h-16 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5a] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
            </div>
            
            <h4 className="font-paperlogy font-bold text-2xl text-gray-800 mb-2">
              벅스어드벤처
            </h4>
            <p className="text-sm text-[#ff6b35] font-semibold mb-4">
              디지털 키즈카페
            </p>
            
            <div className="space-y-2 mb-5">
              <p className="text-gray-700 font-medium">
                대구시 달서구 조암남로32길 20-4 6층
              </p>
              <p className="text-lg font-bold text-gray-800">
                📞 053-642-5533
              </p>
            </div>
            
            <div className="flex gap-3">
              <a 
                href="https://map.naver.com/v5/search/벅스어드벤처%20키즈카페"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-r from-[#03C75A] to-[#00B841] text-white px-4 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
                네이버 지도
              </a>
              <a 
                href="https://map.kakao.com/?q=벅스어드벤처%20키즈카페"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-r from-[#FEE500] to-[#FFCD00] text-gray-800 px-4 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
                카카오맵
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{ width, height }} 
      className={`rounded-2xl overflow-hidden border border-[#eaeaea] relative ${className}`}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff6b35] mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">지도 로딩 중...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}